import React from 'react';

import {Grid, SelectedParentStorable} from 'constants/SampleConstants';
import {BenchlingAction} from 'redux-stores/Action';
// @ts-expect-error
import parseChanges from 'samples/grids/upload-containers/parseChanges';
import {
  Change,
  ChangeStats,
  MODAL_KEYS,
  MODAL_ORDER,
} from 'samples/grids/upload-containers/UploadContainersFlowConstants';
import {UploadContainersActionType} from 'samples/grids/upload-containers/UploadContainersReduxActions';
import reduceGridUI, {AllGridsUIState} from 'samples/reduceGridUI';
import {getEmptySelectedParent} from 'samples/storables/ParentStorableUtil';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import combineBenchlingReducers from 'util/combineBenchlingReducers';
// @ts-expect-error
import switchPanes from 'util/switchPanes';

export interface GridCellProps {
  tooltipTitle: string | null;
  innerClassName: string;
  dataTestAttrs?: DataTestAttrs;
}

interface UploadContainersFlowState {
  modalKey: string;
  open: boolean;
  rawText: string;
  selectedContainerSchemaId: string | null;
  selectedContainerSourceId: string | undefined;
  inputTextError: React.ReactNode | null;
  schemaError: string | null | undefined;
  projectError: string | undefined;
  isValidating: boolean;

  changes: Array<Change> | null;
  gridPreview: Grid | null;
  spreadsheetUrl: string | null;
  gridCellProps: Array<Array<GridCellProps>> | null;
  changeStats: ChangeStats | null;

  isUploading: boolean;
  selectedParent: SelectedParentStorable;
}

export interface SamplesUIState {
  uploadContainersFlow: UploadContainersFlowState;
  gridUI: AllGridsUIState;
}

export const UPLOAD_CONTAINERS_FLOW_INITIAL_STATE: UploadContainersFlowState = {
  modalKey: MODAL_KEYS.MATRIX_INPUT,
  open: false,

  rawText: '',
  selectedContainerSchemaId: null,
  selectedContainerSourceId: undefined,
  inputTextError: null,
  schemaError: null,
  projectError: undefined,
  isValidating: false,

  changes: null,
  gridPreview: null,
  spreadsheetUrl: null,
  gridCellProps: null,
  changeStats: null,

  isUploading: false,
  selectedParent: getEmptySelectedParent(),
};

function switchUploadContainersPanes({
  currentPaneKey,
  offset,
}: {
  currentPaneKey: string;
  offset: number;
}): string {
  return switchPanes(MODAL_ORDER, {currentPaneKey, excludedPanes: [], offset});
}

export function reduceUploadContainersFlow(
  state: UploadContainersFlowState = UPLOAD_CONTAINERS_FLOW_INITIAL_STATE,
  action: BenchlingAction
): UploadContainersFlowState {
  switch (action.type) {
    case UploadContainersActionType.OPEN_UPLOAD_CONTAINERS_FLOW: {
      return {...UPLOAD_CONTAINERS_FLOW_INITIAL_STATE, open: true};
    }
    case UploadContainersActionType.UPLOAD_CONTAINERS_COMPLETED:
    case UploadContainersActionType.UPLOAD_CONTAINERS_FAILED:
    case UploadContainersActionType.CLOSE_UPLOAD_CONTAINERS_FLOW: {
      return {...state, open: false};
    }
    case UploadContainersActionType.UPLOAD_CONTAINERS_CHANGE_TEXT: {
      const {rawText} = action;
      return {...state, rawText};
    }
    case UploadContainersActionType.UPLOAD_CONTAINERS_CHANGE_CONTAINER_SCHEMA: {
      const {selectedContainerSchemaId} = action;
      return {...state, selectedContainerSchemaId};
    }
    case UploadContainersActionType.UPLOAD_CONTAINERS_CHANGE_CONTAINER_SOURCE: {
      const {selectedContainerSourceId} = action;
      return {...state, selectedContainerSourceId};
    }
    case UploadContainersActionType.UPLOAD_CONTAINERS_FLOW_GO_TO_NEXT: {
      const newModalKey = switchUploadContainersPanes({
        currentPaneKey: state.modalKey,
        offset: +1,
      });
      return {...state, modalKey: newModalKey};
    }
    case UploadContainersActionType.UPLOAD_CONTAINERS_FLOW_GO_TO_PREV: {
      const newModalKey = switchUploadContainersPanes({
        currentPaneKey: state.modalKey,
        offset: -1,
      });
      return {...state, modalKey: newModalKey};
    }
    case UploadContainersActionType.VALIDATE_UPLOAD_CONTAINERS_STARTED: {
      return {...state, inputTextError: null, schemaError: null, isValidating: true};
    }
    case UploadContainersActionType.VALIDATE_UPLOAD_CONTAINERS_COMPLETED: {
      const {changes, gridPreview, spreadsheetUrl} = action;
      const {gridCellProps, changeStats} = parseChanges(gridPreview, changes);
      return {
        ...state,
        inputTextError: null,
        schemaError: null,
        projectError: '',
        isValidating: false,
        changes,
        gridPreview,
        spreadsheetUrl,
        gridCellProps,
        changeStats,
      };
    }
    case UploadContainersActionType.VALIDATE_UPLOAD_CONTAINERS_FAILED: {
      const {inputTextError, schemaError, projectError} = action;
      return {...state, inputTextError, schemaError, projectError, isValidating: false};
    }
    case UploadContainersActionType.UPLOAD_CONTAINERS_STARTED: {
      return {...state, isUploading: true};
    }
    case UploadContainersActionType.UPLOAD_CONTAINERS_SELECT_PARENT: {
      const {selectedParent} = action;
      return {...state, selectedParent};
    }
    case UploadContainersActionType.UPLOAD_CONTAINERS_REMOVE_PARENT: {
      return {...state, selectedParent: getEmptySelectedParent()};
    }
    default:
      return state;
  }
}

export default combineBenchlingReducers({
  uploadContainersFlow: reduceUploadContainersFlow,
  gridUI: reduceGridUI,
});
